import { Link } from "react-router-dom";
import { Play, Pause, Rewind, FastForward, Gauge } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { formatClock } from "@/lib/podcast";

const RATES = [0.8, 1, 1.25, 1.5, 2];

export default function PlayerBar() {
  const { current, isPlaying, time, duration, rate, toggle, seek, skip, setRate } =
    usePlayer();

  if (!current) return null;
  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <div
      data-testid="persistent-player-bar"
      className="fixed bottom-0 inset-x-0 z-50 bg-[#0b1f4d] text-white border-t border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.55)]"
    >
      <input
        type="range"
        min={0}
        max={duration || 0}
        step={1}
        value={time}
        onChange={(e) => seek(Number(e.target.value))}
        data-testid="player-progress-slider"
        aria-label="Seek"
        className="w-full h-1 appearance-none bg-white/15 accent-[#f5b61c] cursor-pointer"
        style={{
          background: `linear-gradient(to right, #f5b61c ${pct}%, rgba(255,255,255,0.15) ${pct}%)`,
        }}
      />
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-3 sm:gap-4">
        <Link
          to={`/episodes/${current.slug}`}
          data-testid="player-episode-link"
          className="flex items-center gap-3 min-w-0 flex-1"
        >
          <img
            src={current.image}
            alt=""
            className="h-11 w-11 rounded-md object-cover ring-1 ring-white/15 shrink-0"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate leading-tight">
              {current.title}
            </p>
            <p className="text-[11px] font-mono text-[#ffd25e]">
              {formatClock(time)} / {formatClock(duration || current.durationSeconds)}
            </p>
          </div>
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          <button
            data-testid="player-skip-back"
            onClick={() => skip(-15)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Back 15 seconds"
          >
            <Rewind size={18} />
          </button>
          <button
            data-testid="player-play-pause"
            onClick={toggle}
            className="grid place-items-center h-11 w-11 rounded-full bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] transition-colors"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </button>
          <button
            data-testid="player-skip-forward"
            onClick={() => skip(15)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Forward 15 seconds"
          >
            <FastForward size={18} />
          </button>

          <button
            data-testid="player-speed-btn"
            onClick={() => {
              const i = RATES.indexOf(rate);
              setRate(RATES[(i + 1) % RATES.length]);
            }}
            className="hidden sm:inline-flex items-center gap-1 ml-1 px-2.5 py-1.5 rounded-full text-xs font-mono bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Playback speed"
          >
            <Gauge size={14} /> {rate}x
          </button>
        </div>
      </div>
    </div>
  );
}
